import { useApi } from '@core/api'
import {
  FeaturesSchema,
  InternalCreateCustomerInput,
  InternalUpdateCustomerInput,
} from '@core/api/generated'
import { useMainState } from '@core/main-state'
import { addSnack } from '@core/snack/snack-state'
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query'

export const keys = {
  getCustomer: ['admin', 'getCustomer'],
  listCustomers: ['admin', 'listCustomers'],
  updateCustomer: ['admin', 'updateCustomer'],
  createCustomer: ['admin', 'createCustomer'],
  switchCustomer: ['admin', 'switchCustomer'],
  featureFlagDefaults: ['admin', 'featureFlagDefaults'],
  featureFlagDefaultsHistory: ['admin', 'featureFlagDefaultsHistory'],
}

export const useCustomer = () => {
  const api = useApi()

  return useSuspenseQuery({
    queryKey: keys.getCustomer,
    queryFn: async () => {
      const { data } = await api.internalGetCustomer({ throwOnError: true })

      return data
    },
  })
}

export const useListCustomers = () => {
  const api = useApi()

  return useSuspenseQuery({
    queryKey: keys.listCustomers,
    queryFn: async () => {
      const { data } = await api.internalListCustomers({ throwOnError: true })
      return data
    },
  })
}

export const useSwitchCustomer = () => {
  const api = useApi()

  return useMutation({
    mutationKey: keys.switchCustomer,
    mutationFn: async (customerId: string): Promise<{ status?: string; message?: string }> => {
      await api.switchCustomer({ path: { customer_id: customerId } })
      return { status: 'ok' }
    },
    onSuccess: () => {
      addSnack({ message: 'Customer switched, refreshing...', severity: 'info' })
    },
    onError: () => {
      const message = 'Failed to switch customer'
      addSnack({ message, severity: 'error' })
      return { status: 'error', message }
    },
  })
}

export const useUpdateCustomer = () => {
  const api = useApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: keys.updateCustomer,
    mutationFn: async (body: InternalUpdateCustomerInput) => {
      const { data } = await api.internalUpdateCustomer({
        body,
        throwOnError: true,
      })

      await queryClient.invalidateQueries({
        queryKey: keys.getCustomer,
      })

      return data
    },
    onSuccess: (_data, variables) => {
      useMainState.setState((prev) => {
        const customer = { ...prev.customer! }
        if (variables.features != null)
          customer.features = variables.features as typeof customer.features
        if (variables.hasOptionRationale != null)
          customer.hasOptionRationale = variables.hasOptionRationale
        return { customer }
      })
      addSnack({ message: 'Customer updated', severity: 'success' })
    },
    onError: (error: Error) => {
      addSnack({ message: error.message, severity: 'error' })
    },
  })
}

export const useCreateCustomer = () => {
  const api = useApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: keys.createCustomer,
    mutationFn: (body: InternalCreateCustomerInput) => {
      return api.internalCreateCustomer({ body })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.listCustomers })
    },
  })
}

export const useFeatureFlagDefaults = () => {
  const api = useApi()

  return useSuspenseQuery({
    queryKey: keys.featureFlagDefaults,
    queryFn: async () => {
      const { data } = await api.getFeatureFlagDefaults({ throwOnError: true })
      return data
    },
  })
}

export const useUpdateFeatureFlagDefaults = () => {
  const api = useApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (body: FeaturesSchema) => {
      await api.updateFeatureFlagDefaults({ body, throwOnError: true })
      await queryClient.invalidateQueries({ queryKey: keys.featureFlagDefaults })
      await queryClient.invalidateQueries({ queryKey: keys.featureFlagDefaultsHistory })
    },
    onSuccess: () => {
      addSnack({ message: 'Defaults updated', severity: 'success' })
    },
    onError: (error: Error) => {
      addSnack({ message: error.message, severity: 'error' })
    },
  })
}

const HISTORY_PAGE_SIZE = 10

export const useFeatureFlagDefaultsHistory = (enabled: boolean = true) => {
  const api = useApi()

  return useInfiniteQuery({
    queryKey: keys.featureFlagDefaultsHistory,
    queryFn: async ({ pageParam = 0 }) => {
      const { data } = await api.getFeatureFlagDefaultsHistory({
        query: { offset: pageParam, limit: HISTORY_PAGE_SIZE },
        throwOnError: true,
      })
      return data
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      if (!lastPage.hasMore) return undefined
      return lastPageParam + HISTORY_PAGE_SIZE
    },
    enabled,
  })
}
